import mixpanel from "mixpanel-browser";

import {
  SOAP_FIELDS,
  CUSTOM_FIELDS,
  TEMPLATE_FIELDS,
  TEMPLATE_TYPES,
} from "./constants";
import {
  cleanExtractedData,
  processCustomFields,
  processSoapFields,
} from "./TemplateBuilder/utils";

export const createNewTemplate = async (
  templateType,
  isFavourite = true,
  createAutoTemplate,
) => {
  const createTemplateInput = { templateType, isFavourite };

  if (templateType === TEMPLATE_TYPES.SOAP) {
    createTemplateInput.templateFields = SOAP_FIELDS;
  } else if (templateType === TEMPLATE_TYPES.CUSTOM) {
    createTemplateInput.templateFields = CUSTOM_FIELDS;
  }
  try {
    const response = await createAutoTemplate({
      variables: { createTemplateInput },
    });
    mixpanel.track("Create template button clicked", {
      templateType,
      templateUuid: response?.data?.createAutoTemplate?.templateUuid,
    });
    return response?.data?.createAutoTemplate?.templateUuid;
  } catch (error) {
    alert("error", "Error creating template. Please try again.");
    mixpanel.track("Template creation error", {
      error: error.message,
    });
  }
};

/**
 * Assembles dropdown options with section headers for the template preset dropdown.
 * Used by both the classic builder's TemplateExamplesChipMenu and the wizard's StartingPointStep.
 */
export function buildTemplateDropdownOptions({
  defaultOptions = [],
  libraryOptions = [],
  myTemplateOptions = [],
  sharedTemplateOptions = [],
}) {
  const options = [];

  if (defaultOptions.length > 0) {
    options.push({
      isHeader: true,
      label: "Scribenote Defaults",
      value: "scribenote-defaults-header",
    });
    options.push(...defaultOptions);
  }

  if (libraryOptions.length > 0) {
    options.push({
      isHeader: true,
      label: "Template Library",
      value: "template-library-header",
    });
    options.push(...libraryOptions);
  }

  if (myTemplateOptions.length > 0) {
    options.push({
      isHeader: true,
      label: "My Templates",
      value: "my-templates-header",
    });
    options.push(...myTemplateOptions);
  }

  if (sharedTemplateOptions.length > 0) {
    options.push({
      isHeader: true,
      label: "Shared with me",
      value: "shared-templates-header",
    });
    options.push(...sharedTemplateOptions);
  }

  return options;
}

/**
 * Applies a built-in (non-user) preset template to the form.
 * Handles cleanExtractedData, field processing, and setValue calls.
 */
export function applyBuiltInPreset(setValue, preset, templateType) {
  const cleanedContent = cleanExtractedData(preset);
  if (templateType === TEMPLATE_TYPES.CUSTOM) {
    setValue(
      "templateFields",
      processCustomFields(cleanedContent[TEMPLATE_FIELDS.CUSTOM]),
      { shouldDirty: true },
    );
  } else {
    setValue("templateFields", processSoapFields(cleanedContent), {
      shouldDirty: true,
    });
  }
  setValue("userPreferences", preset.preferences || "");
  if (preset.sampleTranscript) {
    setValue("sampleTranscript", preset.sampleTranscript);
  }
}

/**
 * Resets the form to blank fields for a given template type.
 */
export function resetTemplateFields(setValue, templateType) {
  setValue("templateType", templateType);
  setValue(
    "templateFields",
    templateType === TEMPLATE_TYPES.SOAP
      ? SOAP_FIELDS
      : CUSTOM_FIELDS,
  );
  setValue("userPreferences", "");
}

/**
 * Parses preview content for display with DSNoteDisplay.
 * For SOAP templates, parses into individual fields (Subjective, Objective, Assessment, Plan).
 * For non-SOAP templates, returns freeform content.
 * Backend SOAP format: "Subjective:\n<content>\n\nObjective:\n<content>\n\n..."
 */
export function parsePreviewContent(previewContent, isSoap = true) {
  if (!previewContent) return { content: "" };
  if (!isSoap) return { content: previewContent };

  // Match section headers like "Subjective:", "**Subjective:**", etc.
  const sectionPattern =
    /^(?:\*\*)?(Subjective|Objective|Assessment|Plan)(?:\*\*)?:\s*$/im;

  const lines = previewContent.split("\n");
  const sections = {};
  let currentSection = null;
  let currentContent = [];

  for (const line of lines) {
    const match = line.match(sectionPattern);
    if (match) {
      // Save previous section
      if (currentSection) {
        sections[currentSection] = currentContent.join("\n").trim();
      }
      currentSection = match[1].toLowerCase();
      currentContent = [];
    } else if (currentSection) {
      currentContent.push(line);
    }
  }

  // Save last section
  if (currentSection) {
    sections[currentSection] = currentContent.join("\n").trim();
  }

  // If no sections were found, fall back to freeform
  if (Object.keys(sections).length === 0) {
    return { content: previewContent };
  }

  return {
    subjective: sections.subjective || "",
    objective: sections.objective || "",
    assessment: sections.assessment || "",
    plan: sections.plan || "",
  };
}
